import { useMemo } from 'react';
import { usePriceForChain } from '../../../../hooks/usePricing';
import { AddressHeaderProps } from './types';
import { BlockchainAvatar } from './BlockchainAvatar';
import { BalanceDisplay } from './BalanceDisplay';
import { Badge } from 'src/components/ui/badge';
import { AddressToolbar } from '../AddressToolbar/AddressToolbar';
import { useAttributionForAddress } from 'src/hooks/attributions/useAttributionForAddress';
import { useAddressAndChainParams } from 'src/hooks/routing/useAddressAndChainParams';
import { LockedAttributionHoverCard } from 'src/components/Attribution/LockedAttributionHoverCard';
import { isLockedAttribution } from '../../utils';
import { useAddressData } from 'src/hooks/useAddressData';
import { Attribution } from 'src/interfaces';

export const AddressProfile = () => {
  const { address, chain } = useAddressAndChainParams();
  const { attribution } = useAttributionForAddress(chain, address);
  const { price } = usePriceForChain(chain);

  // Get address data (balance and address_type for Bitcoin)
  const { data: addressData } = useAddressData(address, chain);

  // Extract data from hooks
  const { balance, addressType } = useMemo(() => {
    const summary = addressData?.summary;

    return {
      balance: summary?.balance,
      addressType:
        summary && 'address_type' in summary
          ? (summary.address_type as string)
          : undefined,
    };
  }, [addressData]);

  if (!address) {
    return (
      <div className="text-sm text-red-500">
        Invalid address. Please check the URL.
      </div>
    );
  }

  return (
    <div className="mb-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
      <BlockchainAvatar chain={chain} />

      <div className="flex w-full flex-1 flex-col gap-1">
        <AddressHeader address={address} attribution={attribution} />

        <BalanceDisplay balance={balance} chain={chain} price={price} />

        <AddressTags addressType={addressType} attribution={attribution} />
      </div>
    </div>
  );
};

export const AddressHeader = ({ address, attribution }: AddressHeaderProps) => {
  const renderContent = () => {
    const { entity, display_name } = attribution ?? {};

    // Determine what to show in header
    const headerValue = display_name || entity;

    if (headerValue && !isLockedAttribution(headerValue)) {
      return headerValue.charAt(0).toUpperCase() + headerValue.slice(1);
    }

    // If locked OR no attribution, show address
    const addressDisplay = (
      <div className="font-mono text-foreground/90">
        {address?.substring(0, 8)}...
        {address?.substring(address.length - 8)}
      </div>
    );

    if (headerValue && isLockedAttribution(headerValue)) {
      return (
        <div className="flex items-center gap-2">
          {addressDisplay}
          <LockedAttributionHoverCard
            attributionValue={headerValue}
            attributionType="entity"
            iconSize="md"
            fallback={null}
          />
        </div>
      );
    }

    // No attribution - just show address
    return addressDisplay;
  };

  return (
    <div className="flex flex-col sm:flex-row sm:justify-between">
      <div className="mb-2 text-2xl font-extrabold text-foreground sm:mb-0">
        {renderContent()}
      </div>
      <AddressToolbar
        addressData={{ entity: attribution?.entity ?? undefined }}
      />
    </div>
  );
};

export const AddressTags = ({
  addressType,
  attribution,
}: {
  addressType: string | undefined;
  attribution: Attribution | undefined;
}) => {
  const { entity, display_name } = attribution ?? {};

  // Show entity in tags only if there's a display_name AND entity is not locked
  const shouldShowEntity =
    display_name && entity && !isLockedAttribution(entity);

  // Don't show locked entity types
  const shouldShowEntityType =
    attribution?.entity_type &&
    attribution.entity_type !== 'whitelist_locked' &&
    attribution.entity_type !== 'premium_locked';

  if (!addressType && !shouldShowEntity && !shouldShowEntityType) {
    return null;
  }

  return (
    <div className="mt-1 flex items-center gap-2">
      {addressType && (
        <Badge variant="secondary" className="text-foreground/90">
          {addressType}
        </Badge>
      )}
      {shouldShowEntity && (
        <Badge variant="secondary" className="text-foreground/90">
          {entity}
        </Badge>
      )}
      {shouldShowEntityType && (
        <Badge variant="secondary" className="text-foreground/90">
          {attribution.entity_type}
        </Badge>
      )}
    </div>
  );
};
